<template>
  <ResourceDialog
    @close="close"
    ref="invoiceDialog"
    :cancellable="false"
    :max-width="null"
    :title="title"
    save-button-text="Done"
    text-class="bg-super-light-blue pa-4"
    close-on-save
    closeable
    fullscreen
  >
    <template #header>
      <v-col
        class="position-relative"
        data-testId="invoice-editor-header"
      >
        <div class="d-flex flex-column position-relative">
          <div class="d-flex ga-2 align-center wrap-text">
            <v-btn
              @click="close()"
              :aria-label="$bct('Close dialog')"
              icon="arrow_back"
              variant="text"
            />

            <span>{{ $bct(title) }}</span>

            <v-chip
              v-if="invoiceStatus"
              v-bind="invoiceStatusPresentation"
              label
            >
              {{ invoiceStatus }}
            </v-chip>

            <span
              v-if="invoice?.auto_approved_at && latestRuleSetResult?.passed"
              class="fs-14 c-light-black ml-2"
            >
              {{ $bct('Automatically approved at') }}
              <LongDateTime :date="invoice.auto_approved_at" />
            </span>

            <span
              v-else-if="latestRuleSetResult?.evaluated_at"
              class="fs-14 c-light-black auto-approval-text ml-2"
            >
              {{ $bct('Auto-approval run on') }}
              <LongDateTime :date="latestRuleSetResult.evaluated_at" />
            </span>

            <RuleSetResultsPopover
              v-if="latestRuleSetResult"
              ref="ruleSetPopover"
              :rule-set-result="latestRuleSetResult"
              class="ml-1"
            />
          </div>
        </div>
      </v-col>
    </template>

    <template #form>
      <div
        v-if="invoice"
        class="bg-super-light-blue fill-height d-flex"
      >
        <div
          :style="rightBarTab ? 'width: 65%;' : 'width: 100%'"
          class="d-flex flex-column ga-4 fill-height"
        >
          <RuleSetResultsCard
            :invoice="invoice"
            :rule-set-result="latestRuleSetResult"
          />

          <v-card
            v-if="invoice?.application_type === 'Subsidy'"
            border
            tile
          >
            <v-card-text class="fs-16 pa-4">
              <v-row align="center">
                <v-col>
                  <p class="my-1 mx-2">
                    <strong>{{ $bct('Application') }}: </strong>

                    <router-link
                      :to="{
                        name: 'SubsidyShow',
                        params: { id: invoice.application_id },
                      }"
                      target="_blank"
                    >
                      {{ invoice.application_name }}
                    </router-link>
                  </p>

                  <p class="my-1 mx-2">
                    <strong>{{ $bct('Business') }}: </strong>

                    <router-link
                      :to="{ name: 'BusinessShow', params: { id: invoice.business_id } }"
                      target="_blank"
                    >
                      {{ invoice.business_name }}
                    </router-link>
                  </p>

                  <p
                    v-if="invoice.provider_id && invoice.provider_name"
                    class="my-1 mx-2"
                  >
                    <strong>{{ $bct('Provider') }}: </strong>

                    <router-link
                      :to="{ name: 'ProviderShow', params: { providerId: invoice.provider_id } }"
                      target="_blank"
                    >
                      {{ invoice.provider_name }}
                    </router-link>
                  </p>
                  <p
                    v-if="invoice.start_date && invoice.finish_date"
                    class="my-1 mx-2"
                  >
                    <strong>{{ $bct('Dates') }}: </strong>

                    <LongDate :date="invoice.start_date" />
                    -
                    <LongDate :date="invoice.finish_date" />
                  </p>
                </v-col>

                <v-col class="text-right align-self-start">
                  <v-btn
                    v-if="subsidyProgram?.enable_linking_attendance_logs_to_invoices"
                    @click="rightBarTab = !rightBarTab"
                    :disabled="rightBarTab"
                    color="primary"
                    data-testId="invoice-attendance-button"
                    variant="outlined"
                  >
                    {{ $bct('View') }}
                    {{ $bct(terms.attendance) }}
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <v-card
            v-if="!isItemizedAward"
            border
            tile
          >
            <v-card-text>
              <v-row class="ma-0">
                <v-col cols="3">
                  <h5>{{ $bct('Invoice amount') }}</h5>
                </v-col>

                <v-col cols="3">
                  <h5>{{ $bct('Line items total') }}</h5>
                </v-col>

                <v-col cols="3">
                  <h5>{{ $bct('Paid') }}</h5>
                </v-col>

                <v-col cols="3">
                  <h5>{{ $bct('Balance') }}</h5>
                </v-col>
              </v-row>

              <v-divider />

              <v-row class="ma-0 fs-16">
                <v-col cols="3">
                  {{ currency(invoice.amount) }}
                </v-col>
                <v-col cols="3">
                  {{ currency(invoice.line_items_total) }}
                </v-col>
                <v-col cols="3">
                  {{ currency(invoice.amount_paid) }}
                </v-col>
                <v-col cols="3">
                  {{ currency(invoice.amount_unpaid) }}
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <v-card
            border
            tile
          >
            <template #title>
              <div class="d-flex align-center justify-space-between pb-1">
                <h5>{{ $bct('Invoice details') }}</h5>

                <v-btn
                  v-if="allowAddLineItems"
                  @click="openNewInvoiceLineItem"
                  color="primary"
                  prepend-icon="add"
                >
                  {{ $bct('Add new line item') }}
                </v-btn>
              </div>
            </template>

            <template #subtitle>
              <div>
                {{
                  $bct(
                    'Add new line items to make changes to the initial invoice amount, such as fines, refunds, or an increase in funding. Line items can be positive or negative.',
                  )
                }}

                <div
                  v-if="invoice.originating_invoice_id"
                  class="mt-3"
                >
                  <strong>{{ $bct('Originated from invoice:') }}</strong>
                  <span
                    @click="open(invoice.originating_invoice_id)"
                    class="c-primary underlined pointer ml-1"
                  >
                    {{ shortId(invoice.originating_invoice_id) }}
                  </span>
                </div>
              </div>
            </template>

            <template #text>
              <template v-if="invoiceLineItems.length > 0">
                <v-row class="ma-0">
                  <TableHeader title="ID" />

                  <TableHeader title="Description" />

                  <TableHeader title="Qty" />

                  <TableHeader title="Rate" />

                  <TableHeader title="Amount" />

                  <TableHeader title="Funding source" />

                  <TableHeader title="Reason" />
                </v-row>

                <v-divider class="mt-2 mb-4" />

                <v-row
                  v-for="invoiceLineItem in invoiceLineItems"
                  :key="invoiceLineItem.id"
                  class="d-flex align-center ma-0"
                >
                  <!-- ID column is not clickable when locked -->
                  <TableCell v-if="!invoiceLineItem.locked && allowAddLineItems">
                    <div
                      @click="openInvoiceLineItem(invoiceLineItem)"
                      class="c-primary underlined pointer"
                    >
                      {{ shortId(invoiceLineItem.id) }}
                    </div>
                  </TableCell>

                  <TableCell v-else>
                    <v-tooltip location="bottom">
                      <template #activator="{ props: tooltipProps }">
                        <span v-bind="tooltipProps">
                          {{ shortId(invoiceLineItem.id) }}
                        </span>
                      </template>

                      <span>
                        {{ $bct('This line item is locked and cannot be edited.') }}
                      </span>
                    </v-tooltip>
                  </TableCell>

                  <TableCell :content="invoiceLineItem.description" />

                  <TableCell :content="invoiceLineItem.quantity" />

                  <TableCell>
                    <div
                      :title="invoiceLineItem.rate_description"
                      class="d-flex align-center pa-3 ma-n3"
                    >
                      <span>{{
                        invoiceLineItem.rate ? currency(invoiceLineItem.rate) : null
                      }}</span>
                      <v-icon
                        v-if="invoiceLineItem.rate_description && invoiceLineItem.rate"
                        :title="invoiceLineItem.rate_description"
                        class="ml-1 text-grey align-self-center"
                        icon="info_outline"
                        size="x-small"
                      />
                    </div>
                  </TableCell>

                  <TableCell
                    :content="invoiceLineItem.amount"
                    transform="currency"
                  />

                  <TableCell :content="fundingSourceName(invoiceLineItem.funding_source_id)" />
                  <TableCell :content="invoiceLineItem.reason" />
                </v-row>
              </template>

              <template v-if="invoiceLineItems.length === 0">
                <v-row class="ma-0">
                  <TableCell>
                    {{ $bct('No adjustments have been made to this invoice.') }}
                  </TableCell>
                </v-row>
              </template>

              <v-divider class="my-4" />

              <v-row class="d-flex justify-end ma-0">
                <v-spacer />

                <v-col cols="auto">
                  <strong>{{ $bct('Invoice total') }}</strong>
                </v-col>

                <v-col cols="auto">
                  <div>{{ currency(invoice.total_amount) }}</div>
                </v-col>
              </v-row>
            </template>
          </v-card>

          <v-card
            v-if="showAdjustments"
            border
            tile
          >
            <template #title>
              <div class="d-flex align-center justify-space-between">
                <h5>{{ $bct('Adjustments') }}</h5>

                <v-btn
                  v-if="allowAdjustments"
                  @click="openNewAdjustmentLineItem"
                  color="primary"
                  prepend-icon="add"
                >
                  {{ $bct('Make adjustment') }}
                </v-btn>
              </div>
            </template>

            <template #subtitle>
              {{
                $bct(
                  'Apply an invoice adjustment to a paid invoice for over- and underpayments. This will generate a new invoice..',
                )
              }}
            </template>

            <v-card-text>
              <template v-if="overPayments.length > 0">
                <v-row class="ma-0 px-4">
                  <TableHeader title="ID" />
                  <TableHeader title="Total amount" />
                  <TableHeader title="Status" />
                  <TableHeader title="Batch ID" />
                </v-row>

                <v-divider class="mt-2 mb-4" />

                <v-row
                  v-for="overPayment in overPayments"
                  :key="overPayment.id"
                  class="d-flex align-center ma-0"
                >
                  <TableCell class="c-primary">
                    <div
                      @click="open(overPayment.id)"
                      class="c-primary underlined pointer"
                    >
                      {{ shortId(overPayment.id) }}
                    </div>
                  </TableCell>

                  <TableCell
                    :content="overPayment.total_amount"
                    transform="currency"
                  />
                  <TableCell>
                    <v-chip
                      v-if="overPayment.status"
                      v-bind="getInvoiceStatusPresentation(overPayment.status)"
                      label
                    >
                      {{ getStatusDisplayValue(overPayment.status) }}
                    </v-chip>
                  </TableCell>
                  <TableCell
                    @click="navigateToInvoiceBatch(overPayment.invoice_batch_id)"
                    :content="shortId(overPayment.invoice_batch_id)"
                    class="c-primary underlined pointer"
                  />
                </v-row>
              </template>
            </v-card-text>
          </v-card>

          <v-card
            v-if="transfers.length > 0"
            border
            tile
          >
            <template #title>
              <h5>
                {{ $bct('Transactions') }}
              </h5>
            </template>

            <template #text>
              <v-row class="ma-0">
                <TableHeader title="ID" />

                <TableHeader title="Amount" />

                <TableHeader title="Date" />
              </v-row>

              <v-divider class="mt-2 mb-4" />

              <v-row
                v-for="transfer in transfers"
                :key="transfer.id"
                class="d-flex align-center ma-0"
              >
                <TableCell
                  :content="shortId(transfer.id)"
                  class="c-primary"
                />

                <TableCell
                  :content="transfer.amount"
                  transform="currency"
                />

                <TableCell :content="transfer.paid_date" />
              </v-row>
            </template>
          </v-card>

          <v-card
            v-if="customFieldNames.length > 0"
            border
            tile
          >
            <template #title>
              <h5>{{ $bct('Additional information') }}</h5>
            </template>

            <v-card-text>
              <v-row>
                <CustomField
                  v-for="fieldName in customFieldNames"
                  @change="changed = true"
                  @input="changed = true"
                  :key="fieldName"
                  :field="fieldName"
                  :model-value="invoice"
                  :readonly="!allowEditingCustomFields"
                  :schema-id="invoice.schema_id"
                  cols="12"
                  lg="4"
                  md="6"
                />
              </v-row>
            </v-card-text>

            <v-card-actions v-show="changed">
              <v-spacer />
              <v-btn
                @click="updateAfterChangingCustomFields"
                :loading="processing"
                color="primary"
              >
                {{ $bct('Save') }}
              </v-btn>
            </v-card-actions>
          </v-card>

          <v-card
            border
            tile
          >
            <v-card-text>
              <p>
                <span v-if="invoice.status === 'draft'">
                  {{ $bct('This has been drafted and has not been approved.') }}
                </span>

                <span v-else-if="invoice.status === 'needs_review'">
                  {{ $bct('This invoice needs review.') }}
                </span>

                <span v-else-if="invoice.status === 'paused'">
                  {{ $bct('This invoice has been paused.') }}
                </span>

                <span v-else-if="invoice.status === 'canceled'">
                  {{ $bct('This invoice has been canceled.') }}
                </span>

                <span v-else-if="invoice.status === 'approved' && isBatched">
                  {{ $bct('This has been approved and will be paid in batch') }}
                  <span
                    @click="navigateToInvoiceBatch(invoice.invoice_batch_id)"
                    class="c-primary underlined pointer"
                  >
                    {{ shortId(invoice.invoice_batch_id) }}
                  </span>
                  .
                </span>

                <span v-else-if="invoice.status === 'approved'">
                  {{ $bct('This has been approved and will be included in the next batch.') }}
                </span>

                <span v-else-if="invoice.status === 'paid'">
                  <template v-if="invoice.invoice_batch_id">
                    {{ $bct('This has been paid in batch') }}
                    <span
                      @click="navigateToInvoiceBatch(invoice.invoice_batch_id)"
                      class="c-primary underlined pointer"
                    >
                      {{ shortId(invoice.invoice_batch_id) }}
                    </span>
                    .
                  </template>
                  <template v-else>
                    {{ $bct('This invoice has been paid.') }}
                  </template>
                </span>

                <span v-else-if="invoice.status === 'processing' && isBatched">
                  {{ $bct('This is processing in batch') }}
                  <span
                    @click="navigateToInvoiceBatch(invoice.invoice_batch_id)"
                    class="c-primary underlined pointer"
                  >
                    {{ shortId(invoice.invoice_batch_id) }} </span
                  >.
                </span>
              </p>
            </v-card-text>

            <v-card-actions
              v-if="allowStatusChange"
              class="pa-8"
            >
              <v-btn
                v-if="invoice.status != 'canceled'"
                @click="cancel"
                :disabled="changed"
                :loading="processing"
                color="red"
              >
                {{ $bct('Cancel invoice') }}
              </v-btn>

              <v-spacer />

              <v-btn
                v-if="allowMoveToDraft"
                @click="updateStatus('draft')"
                :disabled="changed"
                :loading="processing"
                color="primary"
                variant="outlined"
              >
                {{ $bct('Move to Draft') }}
              </v-btn>

              <v-btn
                v-if="allowMoveToNeedsReview"
                @click="updateStatus('needs_review')"
                :disabled="changed"
                :loading="processing"
                color="primary"
                variant="outlined"
              >
                {{ $bct('Move to Needs Review') }}
              </v-btn>

              <v-btn
                v-if="allowPauseInvoice"
                @click="updateStatus('paused')"
                :disabled="changed"
                :loading="processing"
                color="primary"
                variant="outlined"
              >
                {{ $bct('Pause invoice') }}
              </v-btn>

              <v-btn
                v-if="invoice.status != 'approved'"
                @click="updateStatus('approved')"
                :disabled="changed"
                :loading="processing"
                color="primary"
              >
                {{ $bct('Approve invoice') }}
              </v-btn>

              <v-btn
                v-if="canPayInvoiceManually"
                @click="openPaymentModal"
                :disabled="changed"
                :loading="processing"
                color="primary"
              >
                {{ $bct('Pay invoice') }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </div>

        <v-card
          v-if="rightBarTab && subsidyProgram?.enable_linking_attendance_logs_to_invoices"
          :style="'width: 35%;'"
          class="fill-height ml-3"
          data-testId="invoice-attendance-log-section"
          style="overflow-y: auto"
          border
          title
        >
          <template #title>
            <div class="d-flex align-center justify-space-between">
              <h5 tabindex="0">
                {{ `${$bct(capitalize(terms.attendance))} ${$bct('record')}` }}
              </h5>

              <v-btn
                @click="rightBarTab = false"
                aria-label="Close Review"
                class="focus-visible me-n3"
                icon="close"
                size="small"
                variant="text"
              />
            </div>
          </template>

          <v-card-text>
            <div class="position-relative">
              <p class="mb-1 mt-3 mx-2 fs-16">
                <router-link
                  :to="{
                    name: 'SubsidyShow',
                    params: { id: invoice.application_id },
                  }"
                  target="_blank"
                >
                  {{ invoice.application_name }}
                </router-link>
              </p>

              <p class="my-1 mx-2 fs-14">
                {{ `${$bct('ID')} ${childSummary.id}` }}
              </p>

              <p class="my-1 mx-2 fs-14">
                {{ `${$bct('DOB')} ${childSummary.dob}` }}
              </p>
            </div>

            <NullState
              v-if="!attendanceLogs.length"
              :title="`No ${terms.attendance} record.`"
            />

            <InvoiceEditorAttendanceLogs
              v-for="attendanceLog in attendanceLogs"
              :key="attendanceLog.id"
              :attendance-log="attendanceLog"
              :subsidy-program-id="invoice.application_program_id"
            />
          </v-card-text>
        </v-card>
      </div>
    </template>
  </ResourceDialog>

  <ResourceDialog
    @save="createOrUpdateInvoiceLineItem"
    ref="invoiceLineItemDialog"
    :max-width="600"
    :processing="processing"
    :save-button-disabled-function="isInvoiceLineItemSaveDisabled"
    :subtitle="invoiceLineItemModalSubtitle"
    :title="invoiceLineItemModalTitle"
    content-test-id="invoice-editor-line-item-dialog"
  >
    <template #form="{ localValue }">
      <InvoiceLineItemForm
        ref="invoiceLineItemForm"
        :data="localValue"
        :enable-automatic-mode="subsidyProgram?.enable_subsidy_award_invoice_line_items || false"
        :funding-source-required="subsidyProgram?.enable_subsidy_award_invoice_line_items || false"
        :funding-sources="fundingSources"
        :invoice="invoice"
        allow-negative-amount
      />
    </template>
  </ResourceDialog>

  <ResourceDialog
    @save="payInvoice"
    ref="paymentDialog"
    :fields="paymentFields"
    :max-width="500"
    :processing="processing"
    title="Pay invoice"
    close-on-save
  />

  <ConfirmDialog ref="confirmDialog" />
</template>

<script setup>
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';

import { capitalize, shortId } from '@/plugins/filters';
import ConfirmDialog from '@/shared/components/ConfirmDialog.vue';
import { currency } from '@/plugins/filters';
import CustomField from '@/shared/components/CustomField.vue';
import { getInvoiceStatusPresentation, getStatusDisplayValue } from '@/shared/utils/invoiceStatus';
import InvoiceEditorAttendanceLogs from '@/specialist/components/invoice/InvoiceEditorAttendanceLogs.vue';
import InvoiceLineItemForm from '@/specialist/components/invoice/InvoiceLineItemForm.vue';
import LongDate from '@/shared/components/LongDate.vue';
import LongDateTime from '@/shared/components/LongDateTime.vue';
import NullState from '@/shared/components/NullState.vue';
import ResourceDialog from '@/shared/components/form/ResourceDialog.vue';
import RuleSetResultsCard from '@/shared/components/rule-set-results/RuleSetResultsCard.vue';
import RuleSetResultsPopover from '@/shared/components/rule-set-results/RuleSetResultsPopover.vue';
import SubsidyApplicationData from '@/shared/services/subsidy/application-data.js';
import TableCell from '@/shared/components/TableCell.vue';
import TableHeader from '@/shared/components/TableHeader.vue';
import useApi from '@/shared/composables/useApi';
import useEventBus from '@/shared/composables/useEventBus';
import useRolePermissions from '@/shared/composables/useRolePermissions';
import useTerms from '@/shared/composables/useTerms';

const eventBus = useEventBus();
const route = useRoute();
const router = useRouter();
const store = useStore();
const { terms } = useTerms();
const { api: Api } = useApi();
const { paymentsEdit } = useRolePermissions();

const attendanceLogs = ref([]);
const changed = ref(false);
const confirmDialog = ref(null);
const fundingSources = ref([]);
const invoice = ref(null);
const invoiceDialog = ref(null);
const invoiceLineItemDialog = ref(null);
const invoiceLineItemForm = ref(null);
const invoiceLineItemModalTitle = ref('');
const invoiceLineItemModalSubtitle = ref('');
const paymentDialog = ref(null);
const invoiceLineItems = ref([]);
const latestRuleSetResult = ref(null);
const overPayments = ref([]);
const processing = ref(false);
const rightBarTab = ref(false);
const ruleSetResults = ref([]);
const ruleSetPopover = ref(null);
const childApplicationData = ref(null);
const subsidyProgram = ref(null);
const title = ref(null);
const transfers = ref([]);

const paymentFields = [
  {
    value: 'paid_date',
    text: 'Payment date',
    type: 'date',
    required: true,
    description: 'Date when payment was received',
  },
  {
    value: 'check_number',
    text: 'Check number',
    type: 'string',
    required: true,
    description: 'Enter the check number or reference',
    placeholder: 'e.g., 12345',
  },
];

const $bct = inject('translator');

const props = defineProps({
  invoiceBatchId: {
    type: String,
    default: null,
  },
});

const emit = defineEmits(['error', 'chime', 'change']);

defineExpose({ open, close });

const allowEditingCustomFields = computed(() => {
  return (
    paymentsEdit ||
    !isInInvoiceBatchShow.value ||
    !isBatched.value ||
    !['approved', 'processing', 'paid'].includes(invoice.value.status)
  );
});

const allowAddLineItems = computed(() => {
  if (!paymentsEdit) return false;
  if (isInInvoiceBatchShow.value) return false;
  if (isBatched.value) return false;
  if (['canceled', 'processing', 'paid'].includes(invoice.value?.status)) return false;

  return true;
});

const allowAdjustments = computed(() => {
  if (!paymentsEdit) return false;

  if (isItemizedAward.value) {
    return invoice.value?.status === 'paid';
  } else {
    return ['canceled', 'processing', 'paid'].includes(invoice.value?.status);
  }
});

const showAdjustments = computed(() => overPayments.value.length > 0 || allowAdjustments.value);

const noChangesAllowed = computed(
  () => !paymentsEdit || isInInvoiceBatchShow.value || isBatched.value,
);

const allowMoveToDraft = computed(() => {
  return !noChangesAllowed.value && ['paused', 'canceled'].includes(invoice.value?.status);
});

const allowMoveToNeedsReview = computed(() => {
  return !noChangesAllowed.value && invoice.value?.status === 'paused';
});

const allowPauseInvoice = computed(() => {
  return ['draft', 'approved'].includes(invoice.value?.status);
});

const allowStatusChange = computed(() => {
  return !noChangesAllowed.value && !['processing', 'paid'].includes(invoice.value.status);
});

const canPayInvoiceManually = computed(() => {
  // Only allow manual payment for non-ACH invoices that are approved
  // Also excludes batched payments since those are only ACH
  return (
    paymentsEdit &&
    invoice.value?.status === 'approved' &&
    invoice.value?.payee_type !== 'ACH' &&
    !isBatched.value
  );
});

const childSummary = computed(() => {
  const id =
    attendanceLogs.value?.[0]?.meta?.child?.external_id || childApplicationData.value?.external_id;
  const dob = attendanceLogs.value?.[0]?.meta?.child?.dob || childApplicationData.value?.dob;
  return { id, dob };
});

const customFieldNames = computed(() => {
  const schema = store.state.schemas?.[invoice.value?.schema_id];
  const customProperties = schema?.definition?.properties?.custom?.properties ?? {};
  return Object.keys(customProperties);
});

const invoiceStatus = computed(() => getStatusDisplayValue(invoice.value?.status));
const invoiceStatusPresentation = computed(() =>
  getInvoiceStatusPresentation(invoice.value?.status),
);
const isBatched = computed(() => !!invoice.value?.invoice_batch_id);
const isInInvoiceBatchShow = computed(() => !!props.invoiceBatchId);

const isItemizedAward = computed(() => {
  return (
    invoice.value?.application_program_type === 'SubsidyProgram' &&
    subsidyProgram.value?.enable_subsidy_award_invoice_line_items === true
  );
});

function fundingSourceName(fundingSourceId) {
  return fundingSources.value.find((source) => source.id === fundingSourceId)?.name;
}

async function cancel() {
  const confirm = await confirmDialog.value.confirmWithText(
    'Are you certain you want to cancel this invoice?',
  );
  if (!confirm) return;

  await updateStatus('canceled');
}

function openPaymentModal() {
  paymentDialog.value.open({
    paid_date: new Date(),
    check_number: '',
  });
}

async function payInvoice(paymentData) {
  processing.value = true;

  const response = await Api.organization.invoice.update(invoice.value.id, {
    status: 'paid',
    paid_date: paymentData.paid_date,
    check_number: paymentData.check_number,
  });

  processing.value = false;

  if (!response) return;

  invoice.value = response.data;
  eventBus.chime('Invoice marked as paid');
  emit('change');
}

async function createOrUpdateInvoiceLineItem(newVal) {
  processing.value = true;

  const response = await Api.organization.invoice_line_item.createOrUpdate(newVal);
  if (!response?.data) {
    processing.value = false;
    return;
  }

  const isOverPayment = response.data.invoice_id !== invoice.value.id;
  if (isOverPayment) {
    eventBus.chime('The new invoice and line item have been created.');
    invoiceLineItemDialog.value?.close();
    await open(response.data.invoice_id);
  } else if (response?.status === 200 || response?.status === 201) {
    await loadInvoiceLineItems();
    await loadInvoice();

    eventBus.chime('Saved');
    invoiceLineItemDialog.value?.close();
    emit('change');
  }

  processing.value = false;
}

async function loadAttendanceLogs() {
  const response = await Api.shared.attendanceLog.index({ related_invoice_id: invoice.value.id });

  if (response?.data) {
    attendanceLogs.value = response.data;
  }
}

async function loadChildApplicationData() {
  if (attendanceLogs.value.length > 0) return;

  const response = await SubsidyApplicationData.getChildData(invoice.value.application_id);
  if (response?.data) {
    childApplicationData.value = response.data;
  }
}

async function loadInvoice(id = invoice.value.id) {
  const response = await Api.organization.invoice.get(id);
  invoice.value = response.data;
}

async function loadInvoiceLineItems() {
  const response = await Api.organization.invoice_line_item.index({ invoice_id: invoice.value.id });
  invoiceLineItems.value = response.data;
}

async function loadOverPayments() {
  const response = await Api.organization.invoice.index({
    originating_invoice_id: invoice.value.id,
  });
  overPayments.value = response.data;
}

async function loadSubsidyProgram() {
  if (invoice.value?.application_program_type === 'SubsidyProgram') {
    const response = await Api.organization.subsidy_program.get(
      invoice.value.application_program_id,
    );
    subsidyProgram.value = response.data;
  }
}

async function loadFundingSources() {
  if (subsidyProgram.value) {
    const params = { owner_id: subsidyProgram.value.id };
    const response = await Api.organization.funding_source.index(params);
    fundingSources.value =
      response.data?.filter((source) => !source.provider_program_requirements) || [];
  }
}

async function loadTransfers() {
  const response = await Api.organization.fundsTransfer.index({ invoice_id: invoice.value.id });
  transfers.value = response.data;
}

async function loadRuleSetResults() {
  const params = {
    base_record_type: 'Invoice',
    base_record_id: invoice.value.id,
  };

  const response = await Api.shared.ruleSetResult.index(params);

  if (response?.data) {
    ruleSetResults.value = response.data;
    latestRuleSetResult.value = response.data[0]; // API sorts by createdAt
  }
}

async function open(invoiceId) {
  childApplicationData.value = null;
  await loadInvoice(invoiceId);
  title.value = `Invoice #${shortId(invoiceId)}`;

  await loadOverPayments();
  await loadTransfers();
  await loadInvoiceLineItems();
  await loadSubsidyProgram();
  await loadFundingSources();
  await loadRuleSetResults();

  if (subsidyProgram.value?.enable_linking_attendance_logs_to_invoices) {
    await loadAttendanceLogs();
    await loadChildApplicationData();
  }

  await router.push({ query: { ...route.query, invoiceId } });

  invoiceDialog.value?.open();
}

async function close() {
  const query = { ...route.query };
  delete query.invoiceId;

  await router.push({ query });
  invoiceDialog.value?.close();
}

onMounted(async () => {
  if (route.query.invoiceId) {
    await open(route.query.invoiceId);
  }
});

watch(
  () => route.query.invoiceId,
  (newId, oldId) => {
    if (!newId && oldId) invoiceDialog.value?.close();
  },
);

function openNewAdjustmentLineItem() {
  invoiceLineItemModalTitle.value = $bct('Add adjustment');
  invoiceLineItemModalSubtitle.value = $bct(
    'Create a positive amount adjustment to rectify an underpayment and pay out additional funds.',
    // Switch to this text once we support negative invoices:
    // 'Create a positive amount adjustment to rectify an underpayment and pay out additional funds. Create a negative amount adjustment to rectify an overpayment and recoup funds.',
  );
  invoiceLineItemDialog.value.open({ invoice_id: invoice.value.id });
}

function openNewInvoiceLineItem() {
  invoiceLineItemModalTitle.value = $bct('Add new line item');
  invoiceLineItemModalSubtitle.value = null;
  invoiceLineItemDialog.value.open({ invoice_id: invoice.value.id });
}

function openInvoiceLineItem(invoiceLineItem) {
  invoiceLineItemModalTitle.value = $bct('Edit line item');
  invoiceLineItemModalSubtitle.value = null;
  invoiceLineItemDialog.value.open(invoiceLineItem);
}

async function navigateToInvoiceBatch(batchId) {
  if (batchId) {
    await router.push({
      name: 'InvoiceBatchShow',
      params: { id: batchId },
    });
  }
}

function isInvoiceLineItemSaveDisabled() {
  return invoiceLineItemForm.value?.isSaveDisabled?.() ?? true;
}

async function updateAfterChangingCustomFields() {
  await updateInvoice({ data: invoice.value, message: 'Saved' });
}

async function updateInvoice({ data = null, message = 'Saved' }) {
  if (!data) return;
  processing.value = true;

  const response = await Api.organization.invoice.update(invoice.value.id, data);

  if (!response) {
    processing.value = false;
    return;
  }

  invoice.value = response.data;

  invoice.value = response.data;
  changed.value = false;
  eventBus.chime(message);
  emit('change');
  processing.value = false;
}

async function updateStatus(status) {
  await updateInvoice({ data: { status }, message: `Invoice status updated to ${status}` });
}
</script>

<style scoped>
.no-focus-outline:focus {
  outline: none !important;
  box-shadow: none !important;
}

.no-focus-outline::after,
.no-focus-outline::before {
  opacity: 0 !important;
  background-color: transparent !important;
}

.result-item {
  padding: 4px 0;
}

.result-message {
  font-size: 15px;
  line-height: 1.5;
}

.rule-results-card {
  border-left: 4px solid #1976d2;
}

.rule-results-menu-content {
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2) !important;
}

/* Vuetify overrides */
:deep(.v-card-subtitle) {
  opacity: 1 !important;
  font-size: 16px !important;
  white-space: normal !important;
  word-wrap: break-word !important;
  overflow-wrap: break-word !important;
  margin-bottom: 1rem !important;
}
</style>
